// Notification deep links land on /store/<slug>?focusReview=<id>[&focusComment=<id>].
// DOM-free so it can be unit tested; the caller passes window.location.search.

const OBJECT_ID_RE = /^[a-f0-9]{24}$/i;

const FOCUS_KEYS = ["focusReview", "focusComment"] as const;

export type FocusParams = {
  reviewId: string | null;
  commentId: string | null;
};

const validId = (value: string | null): string | null =>
  value && OBJECT_ID_RE.test(value) ? value : null;

// Both values are attacker-controllable, so anything that is not a 24-hex ObjectId is
// dropped before it reaches a fetch or a DOM id lookup. A comment without a review has
// nothing to anchor to, so it is dropped too.
export const parseFocusParams = (search: string): FocusParams => {
  const params = new URLSearchParams(search);
  const reviewId = validId(params.get("focusReview"));
  if (!reviewId) return { reviewId: null, commentId: null };
  return { reviewId, commentId: validId(params.get("focusComment")) };
};

// Whether the search string carries any focus param, valid or not (so junk values get stripped too).
export const hasFocusParams = (search: string): boolean => {
  const params = new URLSearchParams(search);
  return FOCUS_KEYS.some((key) => params.has(key));
};

// Returns the search string without the focus params: "?a=1" or "" when nothing is left.
export const stripFocusParams = (search: string): string => {
  const params = new URLSearchParams(search);
  for (const key of FOCUS_KEYS) params.delete(key);
  const rest = params.toString();
  return rest ? `?${rest}` : "";
};
